import React, { useRef, useState, useEffect } from 'react'
import { aiPoweredImgIcon, oneClick, anoScreen, toolFolder, candidateScreening, userInterview, digitalSignature, database, rocketIcon, fullProcessChart, BrianSimmonsImg, rapidoSvg, HRManagerImg, dashboardPreview } from '../assets/images'
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import 'swiper/css';
import 'swiper/css/navigation';
import { useNavigate } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

const Features = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const bannerRef = useRef(null);
  const featuresGridRef = useRef(null);
  const hiringProcessRef = useRef(null);
  const analyticsRef = useRef(null);
  const testimonialRef = useRef(null);
  const ctaRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    const bannerElements = bannerRef.current?.querySelectorAll('.ats-content-wrapper > *:not(.btn)');
    const button = bannerRef.current?.querySelector('.btn');
    
    if (bannerElements) {
      gsap.fromTo(bannerElements,
        {
          y: 50,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.2,
          ease: "power1.out"
        }
      );
    }

    if (button) {
      gsap.fromTo(button,
        {
          y: 30,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          delay: 0.8,
          ease: "power2.out"
        }
      );
    }

    // Features Grid Animation
    const featureCards = featuresGridRef.current?.querySelectorAll('.feature-card');
    const gridHeader = featuresGridRef.current?.querySelector('.features-grid-header');

    if (gridHeader) {
      gsap.fromTo(gridHeader,
        {
          y: 50,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: gridHeader,
            start: "top 80%",
            once: true,
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );
    }

    if (featureCards) {
      gsap.fromTo(featureCards,
        {
          y: 60,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.1,
          scrollTrigger: {
            trigger: featureCards[0],
            start: "top 80%",
            once: true,
            toggleActions: "play none none reverse"
          },
          ease: "power2.out"
        }
      );
    }

    // Hiring Process Section Animation
    const processTitle = hiringProcessRef.current?.querySelector('#hiring-process-title');
    const processDescription = hiringProcessRef.current?.querySelector('#hiring-process-description');
    const processSteps = hiringProcessRef.current?.querySelectorAll('.step');

    if (processTitle) {
      gsap.fromTo(processTitle,
        {
          y: 50,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: processTitle,
            start: "top 80%",
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );
    }

    if (processDescription) {
      gsap.fromTo(processDescription,
        {
          y: 40,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          delay: 0.2,
          scrollTrigger: {
            trigger: processDescription,
            start: "top 80%",
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );
    }

    if (processSteps) {
      gsap.fromTo(processSteps,
        {
          x: -50,
          opacity: 0
        },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          scrollTrigger: {
            trigger: processSteps[0],
            start: "top 80%",
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );

      // Animate the connecting line
      gsap.fromTo('.hiring-process-steps',
        {
          '--line-width': '0%'
        },
        {
          '--line-width': '34%',
          duration: 1.2,
          delay: 0.5,
          scrollTrigger: {
            trigger: processSteps[0],
            start: "top 80%",
            toggleActions: "play none none reverse"
          },
          ease: "power2.inOut"
        }
      );
    }

    // Analytics Section Animation
    const analyticsTitle = analyticsRef.current?.querySelector('#analytics-title');
    const analyticsDescription = analyticsRef.current?.querySelector('#analytics-description');
    const analyticsCards = analyticsRef.current?.querySelectorAll('.analytics-card');
    const progressCircles = analyticsRef.current?.querySelectorAll('.analytics-progress circle:last-child');

    if (analyticsTitle) {
      gsap.fromTo(analyticsTitle,
        {
          y: 50,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: analyticsTitle,
            start: "top 80%",
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );
    }

    if (analyticsDescription) {
      gsap.fromTo(analyticsDescription,
        {
          y: 40,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          delay: 0.2,
          scrollTrigger: {
            trigger: analyticsDescription,
            start: "top 80%",
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );
    }

    if (analyticsCards) {
      gsap.fromTo(analyticsCards,
        {
          y: 60,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.2,
          scrollTrigger: {
            trigger: analyticsCards[0],
            start: "top 80%",
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );
    }

    if (progressCircles) {
      progressCircles.forEach((circle, index) => {
        const length = circle.getTotalLength();
        gsap.fromTo(circle,
          {
            strokeDasharray: length,
            strokeDashoffset: length
          },
          {
            strokeDashoffset: 0,
            duration: 1.5,
            delay: 0.5 + (index * 0.2),
            scrollTrigger: {
              trigger: circle,
              start: "top 80%",
              toggleActions: "play none none none"
            },
            ease: "power2.inOut"
          }
        );
      });
    }

    // Testimonial Section Animation
    const testimonialTitle = testimonialRef.current?.querySelector('.text-display-1');
    const testimonialControls = testimonialRef.current?.querySelector('.features-testimonial-slider-controls');
    const testimonialCards = testimonialRef.current?.querySelectorAll('.features-testimonial-card');

    if (testimonialTitle) {
      gsap.fromTo(testimonialTitle,
        {
          y: 50,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: testimonialTitle,
            start: "top 80%",
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );
    }

    if (testimonialControls) {
      gsap.fromTo(testimonialControls,
        {
          x: -30,
          opacity: 0
        },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          delay: 0.3,
          scrollTrigger: {
            trigger: testimonialControls,
            start: "top 80%",
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );
    }

    if (testimonialCards) {
      gsap.fromTo(testimonialCards,
        {
          y: 60,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.2,
          scrollTrigger: {
            trigger: testimonialCards[0],
            start: "top 80%",
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );
    }

    // CTA Section Animation
    const ctaTitle = ctaRef.current?.querySelector('#features-cta-title');
    const ctaDescription = ctaRef.current?.querySelector('.text-heading-1');
    const ctaButton = ctaRef.current?.querySelector('.features-cta-btn');
    const ctaImage = ctaRef.current?.querySelector('.features-cta-img');

    if (ctaTitle) {
      gsap.fromTo(ctaTitle,
        {
          y: 50,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: ctaTitle,
            start: "top 80%",
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );
    }

    if (ctaDescription) {
      gsap.fromTo(ctaDescription,
        {
          y: 40,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          delay: 0.2,
          scrollTrigger: {
            trigger: ctaDescription,
            start: "top 80%",
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );
    }

    if (ctaButton) {
      gsap.fromTo(ctaButton,
        {
          y: 30,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          delay: 0.4,
          scrollTrigger: {
            trigger: ctaButton,
            start: "top 80%",
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );
    }

    if (ctaImage) {
      gsap.fromTo(ctaImage,
        {
          scale: 0.95,
          opacity: 0
        },
        {
          scale: 1,
          opacity: 1,
          duration: 1,
          delay: 0.6,
          scrollTrigger: {
            trigger: ctaImage,
            start: "top 80%",
            toggleActions: "play none none none"
          },
          ease: "power2.out"
        }
      );
    }
  }, []);

  return (
    <>
      {/*------------ats-banner-section------------*/}
      <div className="container">
        <section className="ats-banner-section" ref={bannerRef}>
          <div className="ats-banner">
            <div className="ats-content-wrapper">
              <h2 className="text-heading-2 fw-700">Application Tracking System</h2>
              <h4 className="text-heading-1 font-400">Smarter Hiring Starts Here</h4>
              <p className="text-heading-1">
                Revolutionize your recruitment process with Symplora ATS:<br />
                From AI-powered screening to seamless onboarding,<br />
                hire top talent faster, smarter, and without the hassle.
              </p>
              <button className="btn btn--outlined btn-white btn--regular">
                Schedule a Demo
              </button>
            </div>
          </div>
        </section >
      </div>
      {/*------------ats-banner-section------------*/}

      {/*------------features-grid-section------------*/}
      <section className="container features-grid-section" aria-labelledby="features-section-title" ref={featuresGridRef}>
        <div className="features-grid-header text-center">
          <h2 id="features-section-title" className="text-display-1 fw-700 dark-text">Features That Make Hiring Effortless and Effective.</h2>
          <p className="text-heading-1 text-light-1">From AI-powered screening to automated workflows, discover tools that redefine efficiency and fairness in recruitment.</p>
        </div>
        <div className="features-grid">
          <article className="feature-card">
            <div className="feature-icon" aria-hidden="true">
              <img src={aiPoweredImgIcon} alt="AI-Powered CV Parsing" />
            </div>
            <h3 className="text-heading-2 fw-500 dark-text">AI-Powered CV Parsing</h3>
            <p className="text-body dark-text">Intelligent screening that ranks resumes in seconds.</p>
          </article>
          <article className="feature-card">
            <div className="feature-icon" aria-hidden="true">
              <img src={oneClick} alt="One-Click Job Postings" />
            </div>
            <h3 className="text-heading-2 fw-500 dark-text">One-Click Job Postings</h3>
            <p className="text-body dark-text">Post jobs across boards, consultancies, and referrals with one click.</p>
          </article>
          <article className="feature-card">
            <div className="feature-icon" aria-hidden="true">
              <img src={anoScreen} alt="Anonymized Screening" />
            </div>
            <h3 className="text-heading-2 fw-500 dark-text">Anonymized Screening</h3>
            <p className="text-body dark-text">Promote fair hiring by removing candidate-identifying details during early screening.</p>
          </article>
          <article className="feature-card">
            <div className="feature-icon" aria-hidden="true">
              <img src={toolFolder} alt="Standardized Interview Tools" />
            </div>
            <h3 className="text-heading-2 fw-500 dark-text">Standardized Interview Tools</h3>
            <p className="text-body dark-text">Customizable scorecards and kits for consistent candidate evaluation.</p>
          </article>
          <article className="feature-card">
            <div className="feature-icon" aria-hidden="true">
              <img src={candidateScreening} alt="AI-Powered Candidate Screening" />
            </div>
            <h3 className="text-heading-2 fw-500 dark-text">AI-Powered Candidate Screening</h3>
            <p className="text-body dark-text">AI-generated summaries and match scores tailored to role requirements.</p>
          </article>
          <article className="feature-card">
            <div className="feature-icon" aria-hidden="true">
              <img src={userInterview} alt="One-Way Video Interviews" />
            </div>
            <h3 className="text-heading-2 fw-500 dark-text">One-Way Video Interviews</h3>
            <p className="text-body dark-text">Replace phone screens and review candidate videos at your pace.</p>
          </article>
          <article className="feature-card">
            <div className="feature-icon" aria-hidden="true">
              <img src={digitalSignature} alt="Offer Rollouts & Digital Signatures" />
            </div>
            <h3 className="text-heading-2 fw-500 dark-text">Offer Rollouts & Digital Signatures</h3>
            <p className="text-body dark-text">Generate offer letters with automated workflows and integrated e-signatures.</p>
          </article>
          <article className="feature-card">
            <div className="feature-icon" aria-hidden="true">
              <img src={database} alt="Talent CRM Database" />
            </div>
            <h3 className="text-heading-2 fw-500 dark-text">Talent CRM Database</h3>
            <p className="text-body dark-text">Manage all applicants in one organized, searchable database.</p>
          </article>
        </div>
      </section>
      {/*------------features-grid-section------------*/}

      {/*------------hiring-process-section------------*/}
      <section className="hiring-process-section" aria-labelledby="hiring-process-title" ref={hiringProcessRef}>
        <div className="container">
          <h2 id="hiring-process-title" className="text-display-1 fw-700 dark-text text-center mb-4">
            Your Seamless Hiring Process, Powered by Symplora.
          </h2>
          <p id="hiring-process-description" className="text-heading-1 text-light-1 text-center fw-500">
            Step-by-step visual flow showing the journey of a candidate through the Symplora ATS:
          </p>
          <ol className="hiring-process-steps" role="list">
            <li className="step active">
              <span className="step-label text-heading-2 fw-600 primary-text-color">Step 1</span>
              <span className="step-icon"><span role="img" aria-label="rocket"><img className='flex' src={rocketIcon} alt="rocket" /></span></span>
              <h3 className="text-body-1 fw-600 dark-text">Requisition Creation</h3>
              <p className="text-body-1 dark-text">Managers raise a job request <br /> ↓ <br /> Hierarchical approval workflows kick in.</p>
            </li>
            <li className="step active">
              <span className="step-label text-heading-2 fw-600 primary-text-color">Step 2</span>
              <span className="step-icon"><span role="img" className="flex" aria-label="posting"><svg xmlns="http://www.w3.org/2000/svg" width="51" height="50" viewBox="0 0 51 50" fill="none">
                <path d="M31.0233 19L23.5 26.7113L19.976 23.3707L17.5 25.848L23.5 31.6667L33.5 21.4767L31.0233 19Z" fill="#6C4CF1" />
              </svg></span></span>
              <h3 className="text-body-1 fw-600 dark-text">AI-Powered Job Posting</h3>
              <p className="text-body-1 dark-text">One-click posting across job boards, career sites, and referrals.</p>
            </li>
            <li className="step active green">
              <span className="step-label text-heading-2 fw-600 primary-text-color">Step 3</span>
              <span className="step-icon"><span role="img" className="flex" aria-label="screening"><svg xmlns="http://www.w3.org/2000/svg" width="51" height="50" viewBox="0 0 51 50" fill="none">
                <path d="M31.0233 19L23.5 26.7113L19.976 23.3707L17.5 25.848L23.5 31.6667L33.5 21.4767L31.0233 19Z" fill="#36B289" />
              </svg></span></span>
              <h3 className="text-body-1 fw-600 dark-text">Smart Candidate Screening</h3>
              <p className="text-body-1 dark-text">AI parses resumes and ranks candidates based on relevance.</p>
            </li>
            <li className="step">
              <span className="step-label text-heading-2 fw-600 primary-text-color">Step 4</span>
              <span className="step-icon"><span role="img" className="flex" aria-label="feedback"><svg xmlns="http://www.w3.org/2000/svg" width="51" height="50" viewBox="0 0 51 50" fill="none">
                <path d="M31.0233 19L23.5 26.7113L19.976 23.3707L17.5 25.848L23.5 31.6667L33.5 21.4767L31.0233 19Z" fill="#BBD6F5" />
              </svg></span></span>
              <h3 className="text-body-1 fw-600 dark-text">Scheduling & Feedback Management</h3>
              <p className="text-body-1 dark-text">Integrated interview scheduling, scorecards, and real-time feedback sharing.</p>
            </li>
            <li className="step">
              <span className="step-label text-heading-2 fw-600 primary-text-color">Step 5</span>
              <span className="step-icon"><span role="img" className="flex" aria-label="offer"><svg xmlns="http://www.w3.org/2000/svg" width="51" height="50" viewBox="0 0 51 50" fill="none">
                <path d="M31.0233 19L23.5 26.7113L19.976 23.3707L17.5 25.848L23.5 31.6667L33.5 21.4767L31.0233 19Z" fill="#BBD6F5" />
              </svg></span></span>
              <h3 className="text-body-1 fw-600 dark-text">Offer Rollouts</h3>
              <p className="text-body-1 dark-text">Send automated offers with digital signatures and approval workflows.</p>
            </li>
            <li className="step">
              <span className="step-label text-heading-2 fw-600 primary-text-color">Step 6</span>
              <span className="step-icon"><span role="img" className="flex" aria-label="onboarding"><svg xmlns="http://www.w3.org/2000/svg" width="51" height="50" viewBox="0 0 51 50" fill="none">
                <path d="M31.0233 19L23.5 26.7113L19.976 23.3707L17.5 25.848L23.5 31.6667L33.5 21.4767L31.0233 19Z" fill="#BBD6F5" />
              </svg></span></span>
              <h3 className="text-body-1 fw-600 dark-text">Candidate Updates and Onboarding</h3>
              <p className="text-body-1 dark-text">Real-time updates via candidate portal<br /> → Seamless integration with HRMS.</p>
            </li>
          </ol>
        </div>
      </section>
      {/*------------hiring-process-section------------*/}

      {/*------------analytics-section------------*/}
      <section className="analytics-section" aria-labelledby="analytics-title" ref={analyticsRef}>
        <div className="container">
          <h2 id="analytics-title" className="text-display-1 fw-700 dark-text text-center mb-4">
            Make Smarter Decisions with Real-Time Analytics.
          </h2>
          <p id="analytics-description" className="text-heading-1 text-light-1 text-center fw-500">
            Track, analyze, and optimize your hiring funnel with actionable insights.
          </p>
          <div className="analytics-grid">
            <article className="analytics-card">
              <div className="analytics-progress" aria-hidden="true">
                <svg width="140" height="140" viewBox="0 0 140 140">
                  <circle cx="70" cy="70" r="60" stroke="#F2F4F6" strokeWidth="16" fill="none" />
                  <circle cx="70" cy="70" r="60" stroke="url(#gradient)" strokeWidth="16" fill="none" strokeDasharray="377" strokeDashoffset="188.5" transform="rotate(-90 70 70)" />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#591BD1" />
                      <stop offset="100%" stopColor="#A2ADE7" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="analytics-value text-heading-1">50%</span>
              </div>
              <h3 className="text-body fw-700 dark-text">50% Faster Time-to-Hire</h3>
            </article>
            <article className="analytics-card">
              <div className="analytics-progress" aria-hidden="true">
                <svg width="140" height="140" viewBox="0 0 140 140">
                  <circle cx="70" cy="70" r="60" stroke="#F2F4F6" strokeWidth="16" fill="none" />
                  <circle cx="70" cy="70" r="60" stroke="url(#gradient)" strokeWidth="16" fill="none" strokeDasharray="377" strokeDashoffset="301.6" transform="rotate(-90 70 70)" />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#591BD1" />
                      <stop offset="100%" stopColor="#A2ADE7" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="analytics-value text-heading-1">20+</span>
              </div>
              <h3 className="text-body fw-700 dark-text">20+ Hours Saved Per Recruitment Cycle</h3>
            </article>
            <article className="analytics-card">
              <div className="analytics-progress" aria-hidden="true">
                <svg width="140" height="140" viewBox="0 0 140 140">
                  <circle cx="70" cy="70" r="60" stroke="#F2F4F6" strokeWidth="16" fill="none" />
                  <circle cx="70" cy="70" r="60" stroke="url(#gradient)" strokeWidth="16" fill="none" strokeDasharray="377" strokeDashoffset="263.9" transform="rotate(-90 70 70)" />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#591BD1" />
                      <stop offset="100%" stopColor="#A2ADE7" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="analytics-value text-heading-1">30%</span>
              </div>
              <h3 className="text-body fw-700 dark-text">Improved Candidate Quality by 30%</h3>
            </article>
          </div>
          <div className="analytics-btn-wrapper">
            <button className="btn btn--primary btn--regular analytics-btn">
              Unlock Actionable Insights with Symplora
            </button>
          </div>
        </div>
      </section>
      {/*------------analytics-section------------*/}

      {/*------------full-process-chart-section------------*/}
      <section className="full-process-chart-section">
        <div className="container">
          <img src={fullProcessChart} alt="Full Process Chart" />
        </div>
      </section>
      {/*------------full-process-chart-section------------*/}

      {/*------------testimonial-slider-section------------*/}
      <section className="container" ref={testimonialRef}>
        <div className="features-testimonial-slider-section">
          <div className="features-testimonial-slider-container">
            <div className="features-testimonial-slider-left">
              <h2 className="text-display-1 fw-700 light-text mb-6">HR Teams Love Symplora ATS.</h2>
              <div className="features-testimonial-slider-controls">
                <button className="features-testimonial-chevron prev" aria-label="Previous testimonial">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="22" viewBox="0 0 12 22" fill="none">
                    <path d="M11.3736 0.959641C11.7286 1.31466 11.7609 1.87022 11.4704 2.2617L11.3736 2.37385L2.74784 11.0001L11.3736 19.6263C11.7286 19.9813 11.7609 20.5369 11.4704 20.9284L11.3736 21.0405C11.0186 21.3955 10.463 21.4278 10.0716 21.1373L9.9594 21.0405L0.626065 11.7072C0.271043 11.3522 0.238768 10.7966 0.52924 10.4051L0.626065 10.293L9.9594 0.959641C10.3499 0.569117 10.9831 0.569117 11.3736 0.959641Z" fill="#C0D4E3" />
                  </svg>
                </button>
                <span className="features-testimonial-slide-number">{currentTestimonial + 1 < 10 ? `0${currentTestimonial + 1}` : currentTestimonial + 1}</span>
                <button className="features-testimonial-chevron next" aria-label="Next testimonial">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="22" viewBox="0 0 12 22" fill="none">
                    <path d="M0.626145 21.0406C0.271123 20.6856 0.238848 20.13 0.529321 19.7385L0.626145 19.6264L9.25192 11.0002L0.626145 2.37394C0.271123 2.01891 0.238848 1.46336 0.529321 1.07188L0.626145 0.959723C0.981167 0.604701 1.53672 0.572425 1.9282 0.862898L2.04036 0.959723L11.3737 10.2931C11.7287 10.6481 11.761 11.2036 11.4705 11.5951L11.3737 11.7073L2.04036 21.0406C1.64983 21.4311 1.01667 21.4311 0.626145 21.0406Z" fill="white" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="features-testimonial-slider-right">
              <Swiper
                modules={[Navigation]}
                navigation={{
                  nextEl: '.features-testimonial-chevron.next',
                  prevEl: '.features-testimonial-chevron.prev',
                }}
                loop={true}
                className="features-testimonial-swiper"
                onSlideChange={(swiper) => setCurrentTestimonial(swiper.realIndex)}
                onInit={(swiper) => setCurrentTestimonial(swiper.realIndex)}
                slidesPerView={1}
                spaceBetween={20}
              >
                {[
                  {
                    img: HRManagerImg,
                    quote: "Symplora ATS reduced our hiring time by half while improving collaboration across teams. It's a game-changer for our HR workflows",
                    author: 'HR Managers, Recruiters',
                  },
                  {
                    img: HRManagerImg,
                    quote: 'The onboarding process is now seamless and efficient. Our team collaboration has never been better!',
                    author: 'Talent Acquisition Lead',
                  },
                  {
                    img: HRManagerImg,
                    quote: 'We love the analytics and reporting features. It helps us make smarter hiring decisions every day.',
                    author: 'HR Analyst',
                  }
                ].map((testimonial, idx) => (
                  <SwiperSlide key={idx}>
                    <div className="features-testimonial-card">
                      <img className="features-testimonial-avatar" src={testimonial.img} alt={testimonial.author} />
                      <div className="features-testimonial-quote-block">
                        <span className="features-testimonial-quote-mark"><svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                          <path d="M17.6569 7.85383L16.4965 6C8.46304 11.6078 4 18.4207 4 24.0285C4 29.451 7.83821 32 11.0962 32C15.2022 32 18.1032 28.385 18.1032 24.5847C18.1032 21.3868 16.1395 18.6524 13.5063 17.6328C12.7476 17.3547 12.0335 17.123 12.0335 15.779C12.0335 14.0642 13.2385 11.5152 17.6569 7.85383ZM35.3752 7.85383L34.2148 6C26.2706 11.6078 21.7183 18.4207 21.7183 24.0285C21.7183 29.451 25.6457 32 28.9038 32C33.0544 32 36 28.385 36 24.5847C36 21.3868 33.9916 18.6524 31.2692 17.6328C30.5105 17.3547 29.841 17.123 29.841 15.779C29.841 14.0642 31.0907 11.5152 35.3752 7.85383Z" fill="#1C0904" />
                        </svg></span>
                        <span className="features-testimonial-quote text-body-1 dark-text">
                          {testimonial.quote}
                        </span>
                        <div className="features-testimonial-author text-body-1 fw-700 dark-text mt-2">{testimonial.author}</div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </section>
      {/*------------testimonial-slider-section------------*/}

      {/*------------features-cta-section------------*/}
      <section className="container features-cta-section" aria-labelledby="features-cta-title" ref={ctaRef}>
        <div className="features-cta-content">
          <div className="features-cta-left">
            <h2 id="features-cta-title" className="text-display-1 fw-700 white-text">
              Ready to Revolutionize Your Recruitment Process?
            </h2>
            <p className="text-heading-1 white-text">
              Save time, reduce manual effort, and hire smarter with Symplora ATS. Let's show you how.
            </p>
            <button className="btn btn--primary btn--regular features-cta-btn" onClick={() => navigate('/features/ats')}>
              Explore ATS Features
            </button>
          </div>
          <div className="features-cta-right" aria-hidden="true">
            <div className="features-cta-img-wrapper">
              <img
                src={dashboardPreview}
                alt="dashboard-preview"
                className="features-cta-img"
                draggable="false"
              />
              <div className="features-cta-img-gradient"></div>
            </div>
          </div>
        </div>
      </section>
      {/*------------features-cta-section------------*/}
      
    </>
  )
}

export default Features